import {
	EventDispatcher,
	MOUSE,
	Object3D,
	Quaternion,
	Raycaster,
	Spherical,
	TOUCH,
	Vector2,
	Vector3
} from 'three'

/**
 *
 * This control performs orbiting, crawling and dollying/zooming.
 * It mantains the up direction (+Y)
 * Rotate camera: left mouse up and down
 * Crawl: left mouse left and right
 * Zoom: Wheel/pinch. The zoom distance determines the vertical rotation of the camera
 */
const _startEvent = { type: 'start' }
const _endEvent = { type: 'end' }

class CrawlControls extends EventDispatcher {

	constructor(object, domElement, cursor, terrain) {

		super()

		this.object = object
		this.domElement = domElement
		this.domElement.style.touchAction = 'none' // disable touch scroll

		this.cursor = cursor

		this.terrain = terrain

		// Set to false to disable this control
		this.enabled = true

		// Set to true when the camera is controlled by a GSAP animation
		this.isGsapping = false

		// "target" sets the location of focus, where the object orbits around
		this.target = new Vector3()

		// How far you can dolly in and out ( PerspectiveCamera only )
		this.minDistance = 0
		this.maxDistance = Infinity

		// Min and max value for the vertical rotation of the camera.
		// Those extremes will be the values of the rotation when the dolly zoom is at the minmax
		this.minPhi = Math.PI * 0.45
		this.maxPhi = 0

		// Set to true to enable damping (inertia)
		// If damping is enabled, you must call controls.update() in your animation loop
		this.dampingFactor = 0.035
		this.crawlSpeed = 5

		// How far you can crawl on the xz plane (its Vector3 justo for calc purposes)
		this.minCrawl = new Vector3( - Infinity, - Infinity, - Infinity)
		this.maxCrawl = new Vector3(   Infinity,   Infinity,   Infinity)

		// This option actually enables dollying in and out, left as "zoom" for backwards compatibility.
		// Set to false to disable zooming
		this.enableZoom = true
		this.zoomSpeed = 40.0
		this.zoomDampingFactor = 0.03
		this.zoom = this.minDistance

		// Set to false to disable rotating
		this.enableRotate = true
		this.rotateSpeed = 0.18

		//Set to true to make the camera pan a little, to follow the position of the cursor
        this.cursorMovementEnabled = false
        this.cursorMovementDamping = 0.025
		this.cursorMovementDummy = new Object3D()
		this.cursorMovementTarget = new Vector3()

		// Mouse buttons
		this.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY }

		// Touch fingers
		this.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN }

		// the target DOM element for key events
		this._domElementKeyEvents = null

		// Raycaster for height intersection
		this.raycaster = new Raycaster()

		// this method is exposed, but perhaps it would be better if we can make it private...
		this.update = function ()
		{
			const offset = new Vector3()

			// so camera.up is the orbit axis
			const quat = new Quaternion().setFromUnitVectors(object.up, new Vector3( 0, 1, 0))
			const quatInverse = quat.clone().invert()

			return function update()
			{
				const position = scope.object.position
				offset.copy(position).sub(scope.target)

				// rotate offset to "y-axis-is-up" space
				offset.applyQuaternion(quat)

				// angle from z-axis around y-axis
				spherical.setFromVector3(offset)
				spherical.theta += sphericalDelta.theta * scope.dampingFactor

				// Calc and clamp dolly zoom
				scope.zoom += sphericalDelta.radius * scope.zoomDampingFactor
				scope.zoom = Math.max(scope.minDistance, Math.min(scope.maxDistance, scope.zoom))
				spherical.radius = scope.zoom

				// Calc phi angle based on zoom distance
				let phiAngle = (((spherical.radius - scope.minDistance) * (scope.maxPhi - scope.minPhi)) / (scope.maxDistance - scope.minDistance)) + scope.minPhi
				spherical.phi = phiAngle

				// Calc and clamp XZ crawl
				scope.target.addScaledVector(crawlOffset, scope.dampingFactor)
				scope.target.clamp(scope.minCrawl, scope.maxCrawl)

				// Calc Y position
				if(scope.terrain !== undefined && !scope.isGsapping)
				{
					scope.raycaster.set(new Vector3(position.x, 999, position.z), new Vector3(0, -1, 0))
					const intersect = scope.raycaster.intersectObject(scope.terrain, false)
					if(intersect && intersect.length > 0)
					{
						if(position.y < intersect[0].point.y + 0.2)
						{
							scope.target.y = Math.max(intersect[0].point.y, scope.target.y)
						}
					}
				}

				// Apply offset
				spherical.makeSafe()
				offset.setFromSpherical(spherical)

				// Calc mouse movement
				// if(scope.cursorMovementEnabled)
				// {
					// TODO: NON FUNZIONA
					// scope.cursorMovementDummy.position.set(0, 0, 0)
					// scope.cursorMovementDummy.rotation.setFromVector3(scope.object.rotation)
					// scope.cursorMovementDummy.translateX(scope.cursor.x * 0.06)
					// scope.cursorMovementDummy.translateY(scope.cursor.y * 0.04)

					// scope.cursorMovementTarget.x += (scope.cursorMovementDummy.position.x - scope.cursorMovementTarget.x) * scope.cursorMovementDamping
					// scope.cursorMovementTarget.y += (scope.cursorMovementDummy.position.y - scope.cursorMovementTarget.y) * scope.cursorMovementDamping
					// scope.cursorMovementTarget.z += (scope.cursorMovementDummy.position.z - scope.cursorMovementTarget.z) * scope.cursorMovementDamping

					// scope.target.copy(scope.cursorMovementTarget)
				// }



				// rotate offset back to "camera-up-vector-is-up" space
				offset.applyQuaternion(quatInverse)
				position.copy(scope.target).add(offset)
				scope.object.lookAt(scope.target)

				// Update damping
				sphericalDelta.theta *= (1 - scope.dampingFactor)
				sphericalDelta.radius *= (1 - scope.zoomDampingFactor)
				crawlOffset.multiplyScalar(1 - scope.dampingFactor)

				return false
			}

		}()

		this.dispose = function ()
		{
			scope.domElement.removeEventListener('contextmenu', onContextMenu)
			scope.domElement.removeEventListener('pointerdown', onPointerDown)
			scope.domElement.removeEventListener('pointercancel', onPointerCancel)
			scope.domElement.removeEventListener('wheel', onMouseWheel)
			scope.domElement.removeEventListener('pointermove', onPointerMove)
			scope.domElement.removeEventListener('pointerup', onPointerUp)
		}

		/**
		 *
		 * Private
		 */
		const scope = this

		const STATE =
		{
			NONE: - 1,
			ROTATE: 0,
			DOLLY: 1,
			TOUCH_ROTATE: 2,
			TOUCH_DOLLY_PAN: 3
		}

		let state = STATE.NONE

		// current position in spherical coordinates
		const spherical = new Spherical()
		const sphericalDelta = new Spherical()

		const crawlOffset = new Vector3()

		const rotateStart = new Vector2()
		const rotateEnd = new Vector2()
		const rotateDelta = new Vector2()

		const dollyStart = new Vector2()
		const dollyEnd = new Vector2()
		const dollyDelta = new Vector2()

		const pointers = []
		const pointerPositions = {}

		function getZoomScale()
		{
			return Math.pow(0.95, scope.zoomSpeed)
		}

		function rotateLeft(angle)
		{
			if(scope.isGsapping) return
			sphericalDelta.theta -= angle
		}

		function crawlUp(deltaY)
		{
			if(scope.isGsapping) return

			const v = new Vector3()
			v.setFromMatrixColumn(scope.object.matrix, 0)
			v.crossVectors(scope.object.up, v)
			v.multiplyScalar(scope.crawlSpeed * deltaY / scope.domElement.clientHeight)

			crawlOffset.add(v)
		}

		function dollyOut(dollyScale)
		{
			if(scope.isGsapping) return
			sphericalDelta.radius += dollyScale
		}

		function dollyIn(dollyScale)
		{
			if(scope.isGsapping) return
			sphericalDelta.radius -= dollyScale
		}

		/**
		 *
		 * Event Callbacks
		 */
		function handleMouseDownRotate(event) { rotateStart.set(event.clientX, event.clientY) }

		function handleMouseDownDolly(event) { dollyStart.set(event.clientX, event.clientY) }

		function handleMouseMoveRotate(event)
		{
			rotateEnd.set(event.clientX, event.clientY)
			rotateDelta.subVectors(rotateEnd, rotateStart)

			const element = scope.domElement

			rotateLeft(- 2 * Math.PI * rotateDelta.multiplyScalar(scope.rotateSpeed).x / element.clientHeight)
			rotateStart.copy(rotateEnd)

			crawlUp(rotateDelta.y)

			scope.update()
		}

		function handleMouseWheel(event)
		{
			if (event.deltaY < 0) { dollyIn(getZoomScale()) }
			else if (event.deltaY > 0) { dollyOut(getZoomScale()) }

			scope.update()
		}

		function handleTouchStartRotate()
		{
			if (pointers.length === 1)
			{
				rotateStart.set(pointers[ 0 ].pageX, pointers[ 0 ].pageY)

			}
			else
			{
				const x = 0.5 * (pointers[ 0 ].pageX + pointers[ 1 ].pageX)
				const y = 0.5 * (pointers[ 0 ].pageY + pointers[ 1 ].pageY)

				rotateStart.set(x, y)
			}
		}


		function handleTouchStartDolly()
		{
			const dx = pointers[0].pageX - pointers[1].pageX
			const dy = pointers[0].pageY - pointers[1].pageY

			const distance = Math.sqrt(dx * dx + dy * dy)

			dollyStart.set(0, distance)
		}

		function handleTouchMoveRotate(event)
		{
			if (pointers.length == 1)
			{
				rotateEnd.set(event.pageX, event.pageY)
			}
			else
			{
				const position = getSecondPointerPosition(event)
				const x = 0.5 * (event.pageX + position.x)
				const y = 0.5 * (event.pageY + position.y)

				rotateEnd.set(x, y)
			}

			rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed)
			const element = scope.domElement

			rotateLeft( - 4 * Math.PI * rotateDelta.x / element.clientHeight)
			rotateStart.copy(rotateEnd)

			crawlUp(rotateDelta.y)
		}


		function handleTouchMoveDolly(event)
		{
			const position = getSecondPointerPosition(event)

			const dx = event.pageX - position.x
			const dy = event.pageY - position.y

			const distance = Math.sqrt(dx * dx + dy * dy)

			dollyEnd.set(0, distance)
			dollyDelta.set(0, Math.pow( dollyEnd.y / dollyStart.y, scope.zoomSpeed))

			if (dollyDelta.y < 1) { dollyOut(getZoomScale() * 0.2) }
			else if(dollyDelta.y > 1) { dollyIn(getZoomScale() * 0.2) }

			dollyStart.copy(dollyEnd)
		}

		/**
		 *
		 * Event Handlers
		 */
		function onPointerDown(event)
		{
			if (scope.enabled === false) return
			if (pointers.length === 0)
			{
				scope.domElement.setPointerCapture(event.pointerId)

				scope.domElement.addEventListener('pointermove', onPointerMove)
				scope.domElement.addEventListener('pointerup', onPointerUp)
			}

			addPointer(event)
			if(event.pointerType === 'touch') { onTouchStart( event ) }
			else { onMouseDown( event ) }
		}

		function onPointerMove(event)
		{
			if (scope.enabled === false) return
			if ( event.pointerType === 'touch' ) { onTouchMove( event ) }
			else { onMouseMove( event ) }
		}

		function onPointerUp(event)
		{
			if (scope.enabled === false) return
			if (event.pointerType === 'touch') { onTouchEnd() }
			else { onMouseUp(event) }

			removePointer(event)

			if (pointers.length === 0)
			{
				scope.domElement.releasePointerCapture(event.pointerId)

				scope.domElement.removeEventListener('pointermove', onPointerMove)
				scope.domElement.removeEventListener('pointerup', onPointerUp)
			}
		}

		function onPointerCancel(event) { removePointer(event) }

		function onMouseDown(event)
		{
			let mouseAction
			switch (event.button)
			{
				case 0:
					mouseAction = scope.mouseButtons.LEFT
					break
				case 1:
					mouseAction = scope.mouseButtons.MIDDLE
					break
				default:
					mouseAction = - 1
			}

			switch (mouseAction)
			{
				case MOUSE.DOLLY:
					if (scope.enableZoom === false) return
					handleMouseDownDolly(event)
					state = STATE.DOLLY
					break

				case MOUSE.ROTATE:
					if (scope.enableRotate === false) return
					handleMouseDownRotate(event)
					state = STATE.ROTATE
					break
				default:
					state = STATE.NONE
			}

			if (state !== STATE.NONE)
			{
				scope.dispatchEvent(_startEvent)
			}
		}

		function onMouseMove(event)
		{
			if (scope.enabled === false) return
			switch (state)
			{
				case STATE.ROTATE:
					if (scope.enableRotate === false) return
					handleMouseMoveRotate(event)
					break
			}
		}

		function onMouseUp(event)
		{
			scope.dispatchEvent(_endEvent)
			state = STATE.NONE
		}

		function onMouseWheel(event)
		{
			if (scope.enabled === false || scope.enableZoom === false || ( state !== STATE.NONE && state !== STATE.ROTATE)) return

			event.preventDefault()

			scope.dispatchEvent(_startEvent)
			handleMouseWheel(event)
			scope.dispatchEvent(_endEvent)
		}

		function onTouchStart(event)
		{
			trackPointer(event)

			switch (pointers.length)
			{
				case 1:
					switch (scope.touches.ONE)
					{
						case TOUCH.ROTATE:
							if (scope.enableRotate === false) return
							handleTouchStartRotate()
							state = STATE.TOUCH_ROTATE
							break
						default:
							state = STATE.NONE
					}
					break

				case 2:
					if(!scope.enableZoom) return
					handleTouchStartDolly()
					state = STATE.TOUCH_DOLLY_PAN
					break

				default:
					state = STATE.NONE
			}

			if (state !== STATE.NONE)
			{
				scope.dispatchEvent(_startEvent)
			}
		}

		function onTouchMove(event)
		{
			trackPointer(event)

			switch (state)
			{
				case STATE.TOUCH_ROTATE:
					if (scope.enableRotate === false) return
					handleTouchMoveRotate(event)
					scope.update()
					break
				case STATE.TOUCH_DOLLY_PAN:
					if(!scope.enableZoom) return
					handleTouchMoveDolly(event)
					scope.update()
					break

				default:
					state = STATE.NONE
					break
			}

		}

		function onTouchEnd(event)
		{
			scope.dispatchEvent(_endEvent)
			state = STATE.NONE
		}

		function onContextMenu(event)
		{
			// TODO: Cos'è?
			if(scope.enabled === false) return
			event.preventDefault()
		}

		function addPointer(event)
		{
			pointers.push(event)
		}

		function removePointer(event)
		{
			// TODO: Cos'è?
			delete pointerPositions[event.pointerId]

			for (let i = 0; i < pointers.length; i ++)
			{
				if (pointers[ i ].pointerId == event.pointerId)
				{
					pointers.splice(i, 1)
					return
				}
			}
		}

		function trackPointer(event)
		{
			let position = pointerPositions[event.pointerId]
			if (position === undefined)
			{
				position = new Vector2()
				pointerPositions[event.pointerId] = position
			}
			position.set(event.pageX, event.pageY)
		}

		function getSecondPointerPosition(event)
		{
			const pointer = (event.pointerId === pointers[ 0 ].pointerId) ? pointers[ 1 ] : pointers[ 0 ]

			return pointerPositions[pointer.pointerId]
		}

		scope.domElement.addEventListener('contextmenu', onContextMenu)
		scope.domElement.addEventListener('pointerdown', onPointerDown)
		scope.domElement.addEventListener('pointercancel', onPointerCancel)
		scope.domElement.addEventListener('wheel', onMouseWheel, { passive: false })

		// force an update at start
		this.update()
	}

}

export { CrawlControls }
